package com.sddamico.swigmore.ds.poll

import androidx.compose.runtime.Composable
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.SwEffects
import com.sddamico.swigmore.ds.core.Card
import com.sddamico.swigmore.ds.core.CardVariant
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.typeBody
import com.sddamico.swigmore.ds.typeCaps
import com.sddamico.swigmore.ds.typeDisplay
import com.sddamico.swigmore.ds.typeMono
import com.sddamico.swigmore.ds.typeTeam
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * One ranked team on the poll: big rank numeral, team + meta line, points and movement, and a +/−
 * toggle that reveals the [VoteDistribution] and summary readouts ([stats], e.g. "ON 30/30 BALLOTS").
 */
@Composable
fun RankCard(
  rank: Int,
  team: String,
  meta: String,
  points: Int,
  movement: Movement,
  modifier: Modifier = Modifier,
  expanded: Boolean = false,
  distribution: List<Int> = emptyList(),
  stats: List<String> = emptyList(),
  onToggle: (() -> Unit)? = null,
) {
  Card(modifier, variant = CardVariant.Ticket, padding = "12px 14px", radius = SwEffects.radiusCard, stitchInset = "5px") {
    Div(
      attrs = Modifier
        .css("display" to "grid", "grid-template-columns" to "44px minmax(0,1fr) auto auto",
          "align-items" to "center", "gap" to "12px")
        .then(if (onToggle != null) Modifier.css("cursor" to "pointer") else Modifier)
        .toAttrs {
          if (onToggle != null) {
            attr("role", "button")
            attr("tabindex", "0")
            attr("aria-expanded", expanded.toString())
            onClick { onToggle() }
            onKeyDown { if (it.key == "Enter" || it.key == " ") { it.preventDefault(); onToggle() } }
          }
        }
    ) {
      Div(attrs = Modifier.typeDisplay("32px").css("text-align" to "center").toAttrs()) { Text(rank.toString()) }
      Div(attrs = Modifier.css("min-width" to "0").toAttrs()) {
        Div(attrs = Modifier.typeTeam("16px").toAttrs()) { Text(team) }
        Div(attrs = Modifier.typeBody("12px", 600, "normal").css("opacity" to ".7", "margin-top" to "3px").toAttrs()) { Text(meta) }
      }
      Div(attrs = Modifier.css("text-align" to "right").toAttrs()) {
        Div(attrs = Modifier.typeDisplay("20px").toAttrs()) { Text(points.toString()) }
        Div(attrs = Modifier.typeMono("10px", 600).css("margin-top" to "4px", "opacity" to ".75").toAttrs()) { Text(movement.label) }
      }
      if (onToggle != null) {
        Div(
          attrs = Modifier.typeBody("15px", 600, "1").css("width" to "26px", "height" to "26px", "border-radius" to "50%",
            "border" to "1.5px solid ${SwColor.borderOnCream}", "display" to "flex", "align-items" to "center",
            "justify-content" to "center").toAttrs { attr("aria-hidden", "true") }
        ) { Text(if (expanded) "−" else "+") }
      }
    }
    if (expanded) {
      Div(attrs = Modifier.css("margin" to "14px 0 2px", "padding-top" to "12px", "border-top" to "1.5px dashed rgba(29,77,54,.3)").toAttrs()) {
        Div(attrs = Modifier.typeCaps("9px", ".18em").css("margin-bottom" to "8px").toAttrs()) { Text("Votes by ballot position") }
        VoteDistribution(distribution)
        if (stats.isNotEmpty()) {
          Div(
            attrs = Modifier.typeMono("11px").css("display" to "flex", "flex-wrap" to "wrap", "gap" to "6px 18px",
              "margin-top" to "10px", "opacity" to ".8", "text-transform" to "uppercase").toAttrs()
          ) { stats.forEach { Span { Text(it) } } }
        }
      }
    }
  }
}
